import React, { createContext, useContext, useEffect, useState } from 'react';
import { loadGoogleAnalytics, disableGoogleAnalytics } from '../utils/googleAnalytics';

const STORAGE_KEY = 'kk-consent';
const CONSENT_VERSION = 1;

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredConsent(analytics) {
  const consent = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    analytics,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  return consent;
}

// Read-only helper for non-React modules (e.g. api/analytics.js) that need
// to check consent without subscribing to context updates.
export function getStoredConsent() {
  return readStoredConsent();
}

const CookieConsentContext = createContext({
  consent: null,
  hasDecided: false,
  acceptAll: () => {},
  rejectAll: () => {},
  savePreferences: () => {},
  reopen: () => {},
});

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => readStoredConsent());

  // Covers both the initial mount (consent already granted in a prior session)
  // and a live toggle, so a revoke stops gtag without needing a page reload.
  useEffect(() => {
    if (consent?.analytics) {
      loadGoogleAnalytics();
    } else if (consent) {
      disableGoogleAnalytics();
    }
  }, [consent]);

  // `storage` only fires in OTHER tabs, not the one that made the change --
  // this tab already updates state directly via accept/reject/savePreferences.
  // Without this, a reject in tab B would leave GA running in tab A, directly
  // contradicting the choice the user just made in the same browser.
  useEffect(() => {
    function handleStorageChange(event) {
      if (event.key !== STORAGE_KEY) return;
      // Skip while the banner is reopened for re-review here (consent === null):
      // adopting another tab's value now would silently close the banner before
      // the user picked. Their pending choice in this tab wins once submitted.
      if (consent === null) return;
      setConsent(readStoredConsent());
    }

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [consent]);

  const acceptAll = () => setConsent(writeStoredConsent(true));
  const rejectAll = () => setConsent(writeStoredConsent(false));
  const savePreferences = ({ analytics }) => setConsent(writeStoredConsent(!!analytics));
  const reopen = () => setConsent(null);

  return (
    <CookieConsentContext.Provider
      value={{ consent, hasDecided: consent !== null, acceptAll, rejectAll, savePreferences, reopen }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export const useCookieConsent = () => useContext(CookieConsentContext);
